import { useState } from "react";
import { useAddTags, useTags } from "../hooks/usePortfolioTags";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectIds: string[];
}

export function TagManager({ isOpen, onClose, projectIds }: Props) {
  const [tagKey, setTagKey] = useState("");
  const [tagValue, setTagValue] = useState("");
  const { data: existingTags } = useTags();
  const addTagsMutation = useAddTags();

  if (!isOpen) return null;

  const handleAdd = () => {
    if (!tagKey.trim() || !tagValue.trim() || projectIds.length === 0) return;
    addTagsMutation.mutate(
      { projectIds, tags: [{ key: tagKey.trim(), value: tagValue.trim() }] },
      { onSuccess: () => { setTagKey(""); setTagValue(""); onClose(); } },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-surface-700 border border-border p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-h4 text-text-primary">
          Tag {projectIds.length} Project{projectIds.length !== 1 ? "s" : ""}
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-body-sm text-text-secondary mb-1">Tag Key</label>
            <input
              type="text"
              value={tagKey}
              onChange={(e) => setTagKey(e.target.value)}
              className="w-full rounded-lg bg-surface-600 border border-border px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-quantum-500"
              placeholder="e.g. division, priority, program"
              list="tag-keys"
              autoFocus
            />
            {existingTags && existingTags.length > 0 && (
              <datalist id="tag-keys">
                {existingTags.map((t) => (
                  <option key={t.key} value={t.key} />
                ))}
              </datalist>
            )}
          </div>

          <div>
            <label className="block text-body-sm text-text-secondary mb-1">Tag Value</label>
            <input
              type="text"
              value={tagValue}
              onChange={(e) => setTagValue(e.target.value)}
              className="w-full rounded-lg bg-surface-600 border border-border px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-quantum-500"
              placeholder="e.g. AWS, high, datacenter-solar"
              list="tag-values"
            />
            {existingTags && (
              <datalist id="tag-values">
                {existingTags
                  .filter((t) => t.key === tagKey)
                  .flatMap((t) => t.values)
                  .map((v) => (
                    <option key={v} value={v} />
                  ))}
              </datalist>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            className="px-4 py-2 rounded-lg text-body-sm text-text-secondary hover:bg-surface-600 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-lg text-body-sm bg-quantum-500 text-white hover:bg-quantum-600 transition-colors disabled:opacity-50"
            onClick={handleAdd}
            disabled={!tagKey.trim() || !tagValue.trim() || addTagsMutation.isPending}
          >
            {addTagsMutation.isPending ? "Adding..." : "Add Tag"}
          </button>
        </div>
      </div>
    </div>
  );
}
